// 検証ケースの会話（督促チャット）を吹き出しで編集するための純粋な関数。
// 保存形式（ScenarioMessage[]）はそのままで、並び・日時の初期値・貼り付けの取り込みだけを扱う
import { jstDate } from "./caseFacts"
import { REPLY_DELAY_MS, toJstIso } from "./types"
import type { ScenarioMessage } from "./types"

/** 会話履歴の安全上限（サーバの検証と揃える。本番の AI 入力は直近3か月を件数の上限なしで渡す） */
export const MAX_MESSAGES = 1000

/** 1発言の本文の上限（サーバの検証と揃える） */
export const MAX_BODY_LENGTH = 5000

const MINUTE_MS = 60 * 1000

/**
 * 日時順に並べる（同時刻は元の並びのまま）。サーバも日時→入力順で並べて AI に渡すので、
 * 並べ替えても AI への入力は変わらない
 */
export const sortConversation = (messages: ScenarioMessage[]) =>
  messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => Date.parse(a.message.at) - Date.parse(b.message.at) || a.index - b.index)
    .map(({ message }) => message)

/** ISO8601 の時刻。日時の欄を一部消した途中の値（空・不正）は null（画面を落とさない） */
const parsed = (iso: string | undefined) => {
  const time = iso ? Date.parse(iso) : NaN
  return Number.isNaN(time) ? null : time
}

/** 日本時間の時刻（HH:mm）。空・不正な日時は空文字 */
export const jstTime = (iso: string) => {
  const time = parsed(iso)
  return time == null ? "" : toJstIso(time).slice(11, 16)
}

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"]

/** 日付の区切り線の表示（2026年9月19日（土））。空・不正な日時は「日時未入力」 */
export const dayLabel = (iso: string) => {
  const date = jstDate(iso)
  if (!date) return "日時未入力"
  const [year, month, day] = date.split("-").map(Number)
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()]
  return `${year}年${month}月${day}日（${weekday}）`
}

/**
 * 末尾に足す発言の日時の初期値。直前の発言の10分後（続きのケースの返事と同じ間隔）だが、
 * 基準日時より後の発言は保存できないので基準日時で止める。会話が無ければ基準日時。
 * 基準日時・直前の発言の日時が空・不正（欄を消した途中）でも例外にせず、分かる方から決める
 */
export const appendedAt = (lastAt: string | undefined, asOf: string) => {
  const last = parsed(lastAt)
  if (last == null) return asOf
  const limit = parsed(asOf)
  const next = limit == null ? last + REPLY_DELAY_MS : Math.min(last + REPLY_DELAY_MS, limit)
  return toJstIso(Math.max(next, last))
}

/**
 * 発言と次の発言の間に差し込む発言の日時の初期値。10分後が次の発言を越えるなら間（分単位に切り捨て）にする。
 * 同時刻になっても並びは差し込んだ位置のまま
 */
export const insertedAt = (prevAt: string, nextAt: string | undefined, asOf: string) => {
  const prev = parsed(prevAt)
  const next = parsed(nextAt)
  if (prev == null) return nextAt && next != null ? nextAt : asOf
  if (next == null) return appendedAt(prevAt, asOf)
  if (prev + REPLY_DELAY_MS <= next) return toJstIso(prev + REPLY_DELAY_MS)
  const middle = Math.floor((prev + (next - prev) / 2) / MINUTE_MS) * MINUTE_MS
  return toJstIso(Math.max(middle, prev))
}

const SPEAKERS: Record<string, ScenarioMessage["direction"]> = {
  お客様: "inbound",
  当社: "outbound",
}

// 行頭の「[2026/09/19 12:00] お客様:」。日時と括弧は省略できる
const SPEAKER_LINE =
  /^\s*(?:[[［]?\s*(\d{4})[/-](\d{1,2})[/-](\d{1,2})[ T　]+(\d{1,2}):(\d{2})\s*[\]］]?\s*)?(お客様|当社)\s*[:：]\s?(.*)$/

// 本文の先頭の「（画像）」「［受信画像］」は画像の発言にする（続きは補足）
const IMAGE_MARK = /^(?:[（(]画像[）)]|［受信画像］|\[受信画像\])\s*/

const pad = (value: string) => value.padStart(2, "0")

export type ParsedConversation =
  { ok: true; messages: ScenarioMessage[] } | { ok: false; error: string }

/**
 * 「お客様: …」「当社: …」形式の会話テキストを発言にする。行頭に話者の無い行は直前の発言の続き（改行）。
 * 行頭に「2026/09/19 12:00」があればその日時、無ければ直前の発言の10分後（基準日時で止める）。
 * startAt は取り込む会話の直前の発言の日時（無ければ、最後の発言が基準日時になるように前へ並べる）
 */
export function parseConversationText(
  text: string,
  { startAt, asOf }: { startAt?: string; asOf: string }
): ParsedConversation {
  const drafts: {
    line: number
    at: string | null
    direction: ScenarioMessage["direction"]
    lines: string[]
  }[] = []
  for (const [lineIndex, line] of text.replace(/\r\n?/g, "\n").split("\n").entries()) {
    const match = line.match(SPEAKER_LINE)
    if (match) {
      const [, year, month, day, hour, minute, speaker, rest] = match
      const at = year ? `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${minute}:00+09:00` : null
      if (at && Number.isNaN(Date.parse(at)))
        return { ok: false, error: `${lineIndex + 1}行目の日時を読み取れません。` }
      drafts.push({ line: lineIndex + 1, at, direction: SPEAKERS[speaker], lines: [rest] })
    } else if (drafts.length > 0) {
      drafts[drafts.length - 1].lines.push(line)
    } else if (line.trim()) {
      return {
        ok: false,
        error: `${lineIndex + 1}行目が「お客様:」か「当社:」で始まっていません。`,
      }
    }
  }
  if (drafts.length === 0)
    return { ok: false, error: "「お客様: …」「当社: …」の形で会話を貼り付けてください。" }

  // 本文の無い発言は保存できないので、取り込む前に弾く（画像は本文なしでよい）
  for (const draft of drafts) {
    const body = draft.lines.join("\n").trim()
    if (!body) return { ok: false, error: `${draft.line}行目の発言に本文がありません。` }
  }
  const limit = Date.parse(asOf)
  if (Number.isNaN(limit)) return { ok: false, error: "先に基準日時を入力してください。" }
  const start = parsed(startAt)
  let previous = start ?? limit - drafts.filter((draft) => !draft.at).length * REPLY_DELAY_MS
  const messages = drafts.map((draft) => {
    const at = draft.at ? Date.parse(draft.at) : Math.min(previous + REPLY_DELAY_MS, limit)
    previous = at
    const body = draft.lines.join("\n").replace(/^\n+/, "").trimEnd()
    const image = body.match(IMAGE_MARK)
    return {
      at: draft.at ?? toJstIso(at),
      direction: draft.direction,
      contentType: image ? ("image" as const) : ("text" as const),
      body: image ? body.slice(image[0].length) : body,
    }
  })
  const tooLong = messages.findIndex((message) => message.body.length > MAX_BODY_LENGTH)
  if (tooLong >= 0)
    return {
      ok: false,
      error: `${drafts[tooLong].line}行目の発言は${MAX_BODY_LENGTH.toLocaleString("ja-JP")}文字までです。`,
    }
  return { ok: true, messages }
}

/** 発言1件の問題（保存できない理由）。無ければ null */
export function messageProblem(message: ScenarioMessage, asOf: string): string | null {
  const at = Date.parse(message.at)
  if (Number.isNaN(at)) return "日時を入力してください"
  if (at > Date.parse(asOf)) return "基準日時より後の発言です"
  if (message.contentType === "text" && !message.body.trim()) return "本文が空です"
  // 吹き出し・下の入力欄は入力で止まるが、貼り付けは長さを見ないのでここで止める（サーバと同じ上限）
  if (message.body.length > MAX_BODY_LENGTH)
    return `本文は${MAX_BODY_LENGTH.toLocaleString("ja-JP")}文字までです`
  return null
}
